import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useCart } from '../../shared/context/CartContext';
import { useWishlist } from '../../shared/context/WishlistContext';
import CartDrawer from './CartDrawer';
import MobileMenuDrawer from './MobileMenuDrawer';
import LanguageSwitcher from './LanguageSwitcher';
import { useCartDrawer } from '../../shared/context/CartDrawerContext';
import api from '../../shared/services/api';

/** Keeps language control from shrinking; sits next to cart in the top bar */
const HeaderLangSlot = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
`;

interface Collection {
  id: string;
  name: string;
  handle: string;
}

const HeaderContainer = styled.header<{ $isSticky?: boolean }>`
  background-color: ${props => props.theme.colors.white};
  border-bottom: 1px solid ${props => props.theme.colors.border.light};
  position: relative;
  width: 100%;
  z-index: ${props => props.theme.zIndex?.sticky || 1000};
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    position: ${props => props.$isSticky ? 'sticky' : 'relative'} !important;
    top: ${props => props.$isSticky ? '0' : 'auto'} !important;
  }
`;

const HeaderTop = styled.div`
  max-width: ${props => props.theme.layout.containerMaxWidth};
  margin: 0 auto;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.xl};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${props => props.theme.spacing.xl};
  background-color: ${props => props.theme.colors.white};
  height: 70px;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
    gap: ${props => props.theme.spacing.md};
    height: 60px;
  }
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.lg};
  flex: 1;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    flex: 0;
    gap: ${props => props.theme.spacing.sm};
  }
`;

const SearchContainer = styled.div`
  flex: 1;
  max-width: 400px;
  position: relative;
  display: flex;
  align-items: center;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    display: none;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.lg};
  padding-inline-end: 40px;
  border: none;
  border-bottom: 1px solid ${props => props.theme.colors.border.light};
  background-color: transparent;
  color: ${props => props.theme.colors.black};
  font-size: ${props => props.theme.typography.fontSize.sm};
  font-family: ${props => props.theme.typography.fontFamily.body};
  letter-spacing: ${props => props.theme.typography.letterSpacing.wide};
  transition: border-color ${props => props.theme.transitions.base};
  
  &::placeholder {
    color: ${props => props.theme.colors.text.secondary};
    font-weight: ${props => props.theme.typography.fontWeight.light};
  }
  
  &:focus {
    outline: none;
    border-bottom-color: ${props => props.theme.colors.black};
  }
`;

const SearchButton = styled.button`
  position: absolute;
  inset-inline-end: 0;
  background: none;
  border: none;
  cursor: pointer;
  padding: ${props => props.theme.spacing.sm};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.colors.black};
  transition: opacity ${props => props.theme.transitions.base};
  
  &:hover {
    opacity: 0.6;
  }
  
  svg {
    width: 18px;
    height: 18px;
    stroke: currentColor;
  }
`;

const LogoContainer = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  flex-shrink: 0;
  
  img {
    height: 40px;
    width: auto;
    object-fit: contain;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    flex: 1;
    justify-content: center;
    
    img {
      height: 36px;
    }
  }
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.lg};
  flex: 1;
  justify-content: flex-end;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    flex: 0;
    gap: ${props => props.theme.spacing.sm};
  }
`;

const IconButton = styled.button<{ as?: any; to?: string }>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  color: ${props => props.theme.colors.black};
  background: none;
  border: none;
  cursor: pointer;
  padding: ${props => props.theme.spacing.xs};
  transition: opacity ${props => props.theme.transitions.base};
  
  svg {
    width: 20px;
    height: 20px;
    stroke: currentColor;
    fill: none;
    stroke-width: 1.5;
  }
  
  &:hover {
    opacity: 0.6;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding: ${props => props.theme.spacing.xs};
    
    svg {
      width: 18px;
      height: 18px;
    }
    
    /* Hide some icons on mobile */
    &.hide-mobile {
      display: none;
    }
  }
`;

const Badge = styled.span`
  position: absolute;
  top: 2px;
  inset-inline-end: 2px;
  background-color: ${props => props.theme.colors.black};
  color: ${props => props.theme.colors.white};
  font-size: 10px;
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  border-radius: 50%;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
`;

const Navigation = styled.nav`
  border-top: 1px solid ${props => props.theme.colors.border.light};
  max-width: ${props => props.theme.layout.containerMaxWidth};
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.xl};
  height: 48px;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    display: none;
  }
`;

const NavList = styled.ul`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${props => props.theme.spacing.lg};
  list-style: none;
  margin: 0;
  padding: 0;
  height: 100%;
  
  @media (max-width: ${props => props.theme.breakpoints.desktop}) {
    gap: ${props => props.theme.spacing.md};
  }
`;

const NavItem = styled.li`
  flex-shrink: 0;
`;

const NavLink = styled(Link)`
  display: flex;
  align-items: center;
  height: 100%;
  text-decoration: none;
  color: ${props => props.theme.colors.text.primary};
  font-size: ${props => props.theme.typography.fontSize.xs};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  font-family: ${props => props.theme.typography.fontFamily.body};
  text-transform: uppercase;
  letter-spacing: ${props => props.theme.typography.letterSpacing.wider};
  white-space: nowrap;
  position: relative;
  transition: color ${props => props.theme.transitions.base};
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 2px;
    background-color: ${props => props.theme.colors.black};
    transform: scaleX(0);
    transition: transform ${props => props.theme.transitions.base};
  }
  
  &:hover {
    color: ${props => props.theme.colors.black};
    
    &::after {
      transform: scaleX(1);
    }
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  cursor: pointer;
  padding: ${props => props.theme.spacing.xs};
  color: ${props => props.theme.colors.black};
  transition: opacity ${props => props.theme.transitions.base};
  
  &:hover {
    opacity: 0.6;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  svg {
    width: 20px;
    height: 20px;
    stroke: currentColor;
    stroke-width: 1.5;
  }
`;

const MobileSearchButton = styled(IconButton)`
  display: none;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    display: flex;
  }
`;

// Refined SVG Icons
const SearchIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
    <circle cx="11" cy="11" r="8"></circle>
    <path d="m21 21-4.35-4.35"></path>
  </svg>
);

const AccountIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
    <circle cx="12" cy="7" r="4"></circle>
  </svg>
);

const WishlistIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
  </svg>
);

const CartIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
    <circle cx="9" cy="21" r="1"></circle>
    <circle cx="20" cy="21" r="1"></circle>
    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
  </svg>
);

const MenuIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
    <line x1="3" y1="12" x2="21" y2="12"></line>
    <line x1="3" y1="6" x2="21" y2="6"></line>
    <line x1="3" y1="18" x2="21" y2="18"></line>
  </svg>
);

const NewHeader: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const { isOpen: isCartOpen, openDrawer: openCartDrawer, closeDrawer: closeCartDrawer } = useCartDrawer();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [collections, setCollections] = useState<Collection[]>([]);
  const navigate = useNavigate();
  const { itemCount } = useCart();
  const { itemCount: wishlistCount } = useWishlist();

  // Make header sticky on all pages except home page (mobile only)
  const isHomePage = location.pathname === '/';
  const isSticky = !isHomePage;

  useEffect(() => {
    const fetchCollections = async () => {
      try {
        const response = await api.get<any[]>('/collections', {
          params: { limit: 50 }
        });
        const allCollections = (response.data || [])
          .filter(c => c.name && c.productCount > 0)
          .sort((a, b) => (b.productCount || 0) - (a.productCount || 0))
          .map(c => ({
            id: c.id,
            name: c.name,
            handle: c.handle || c.id,
            productCount: c.productCount || 0
          }));
        setCollections(allCollections);
      } catch (error) {
        console.error('Failed to fetch collections:', error);
      }
    };
    fetchCollections();
  }, []);

  const navigationItems = React.useMemo(() => {
    const items: Array<{ text: string; href: string }> = [
      { text: t('header.newIn').toUpperCase(), href: '/products' },
    ];

    const collectionsToShow = collections.slice(0, 6);

    collectionsToShow.forEach(collection => {
      items.push({
        text: collection.name.toUpperCase(),
        href: `/products?collection=${collection.id}`
      });
    });

    items.push({ text: t('header.collections').toUpperCase(), href: '/collections' });
    items.push({ text: t('header.easyReturns').toUpperCase(), href: '/returns' });

    return items;
  }, [collections, t]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  const handleCartClick = (e: React.MouseEvent) => {
    e.preventDefault();
    openCartDrawer();
  };

  return (
    <HeaderContainer $isSticky={isSticky}>
      <HeaderTop>
        <LeftSection>
          <MobileMenuButton onClick={() => setIsMobileMenuOpen(true)}>
            <MenuIcon />
          </MobileMenuButton>

          <SearchContainer>
            <form onSubmit={handleSearch} style={{ width: '100%' }}>
              <SearchInput
                type="text"
                placeholder={t('header.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <SearchButton type="submit">
                <SearchIcon />
              </SearchButton>
            </form>
          </SearchContainer>
        </LeftSection>

        <LogoContainer to="/" onClick={closeCartDrawer}>
          <img src="/Media/logo.png" alt={t('header.logoAlt')} width="139" height="63" />
        </LogoContainer>

        <RightSection>
          <IconButton
            as={Link}
            to="/account"
            onClick={closeCartDrawer}
            className="hide-mobile"
          >
            <AccountIcon />
          </IconButton>

          <IconButton
            as={Link}
            to="/wishlist"
            onClick={closeCartDrawer}
            className="hide-mobile"
          >
            <WishlistIcon />
            {wishlistCount > 0 && <Badge>{wishlistCount}</Badge>}
          </IconButton>

          <HeaderLangSlot>
            <LanguageSwitcher />
          </HeaderLangSlot>

          <IconButton onClick={handleCartClick}>
            <CartIcon />
            {itemCount > 0 && <Badge>{itemCount}</Badge>}
          </IconButton>

          <MobileSearchButton onClick={() => navigate('/products?focus=search')}>
            <SearchIcon />
          </MobileSearchButton>
        </RightSection>
      </HeaderTop>

      <Navigation>
        <NavList>
          {navigationItems.map((item) => (
            <NavItem key={item.href}>
              <NavLink to={item.href} onClick={closeCartDrawer}>
                {item.text}
              </NavLink>
            </NavItem>
          ))}
        </NavList>
      </Navigation>

      <CartDrawer isOpen={isCartOpen} onClose={closeCartDrawer} />
      <MobileMenuDrawer
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
        collections={collections}
      />
    </HeaderContainer>
  );
};

export default NewHeader;
